import { LinkIcon, PlusIcon } from "@phosphor-icons/react";
import { Button } from "@bednbody/ui/components/button";
import { Callout } from "@bednbody/ui/components/callout";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { Input } from "@bednbody/ui/components/input";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@bednbody/ui/components/table";
import { useState } from "react";
import { toast } from "sonner";
import { type GiftpassPackage, packageSavings, peso } from "./giftpass-orders.model";
import { useGiftpassPackages, useSavePackage } from "./giftpass-orders.hooks";

/** The order site. A hidden package is ordered from this link (0373). */
const ORDER_SITE = "https://bednbody.ph/giftpass";

interface Draft {
  id: string | null;
  name: string;
  price: string;
  passes: string;
  freePasses: string;
  active: boolean;
  hidden: boolean;
  sortOrder: number;
}

const toDraft = (p: GiftpassPackage): Draft => ({
  id: p.id,
  name: p.name,
  price: String(Number(p.price)),
  passes: String(p.passes),
  freePasses: String(p.free_passes),
  active: p.active,
  hidden: p.hidden,
  sortOrder: p.sort_order,
});

function PackageRow({
  pkg,
  isSuper,
  onSaved,
}: {
  pkg: Draft;
  isSuper: boolean;
  onSaved?: () => void;
}) {
  const [draft, setDraft] = useState(pkg);
  const save = useSavePackage();
  const dirty = JSON.stringify(draft) !== JSON.stringify(pkg) || draft.id === null;
  const price = Number(draft.price);
  const passes = Number.parseInt(draft.passes, 10);
  const free = Number.parseInt(draft.freePasses, 10);
  const valid =
    draft.name.trim() !== "" &&
    Number.isFinite(price) &&
    price > 0 &&
    Number.isInteger(passes) &&
    passes >= 1 &&
    Number.isInteger(free) &&
    free >= 0;
  const savings = packageSavings(price || 0, passes || 0, free || 0);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = () =>
    save.mutate(
      {
        id: draft.id,
        name: draft.name.trim(),
        price: Math.round(price * 100) / 100,
        passes,
        freePasses: free,
        active: draft.active,
        hidden: draft.hidden,
        sortOrder: draft.sortOrder,
      },
      {
        onSuccess: () => {
          onSaved?.();
          toast.success(
            `${draft.name.trim()} saved — ${
              !draft.active
                ? "off sale"
                : draft.hidden
                  ? "hidden, orderable by link only"
                  : "live on the order site"
            }`,
          );
        },
        onError: (error) => toast.error(error.message),
      },
    );

  return (
    <TableRow>
      <TableCell>
        <Input
          aria-label="Package name"
          type="text"
          maxLength={80}
          className="w-48"
          disabled={!isSuper}
          value={draft.name}
          onChange={(event) => set({ name: event.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Price in pesos"
          type="number"
          min={1}
          step="0.01"
          className="w-28 text-right tabular-nums"
          disabled={!isSuper}
          value={draft.price}
          onChange={(event) => set({ price: event.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Passes"
          type="number"
          min={1}
          step={1}
          className="w-20 text-right"
          disabled={!isSuper}
          value={draft.passes}
          onChange={(event) => set({ passes: event.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Free passes"
          type="number"
          min={0}
          step={1}
          className="w-20 text-right"
          disabled={!isSuper}
          value={draft.freePasses}
          onChange={(event) => set({ freePasses: event.target.value })}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {savings.percent > 0 ? `${savings.percent}% off` : "—"}
        <span className="block text-xs text-muted-foreground">
          {savings.total > 0 ? `${peso(Math.round(savings.perPass))} a pass` : ""}
        </span>
      </TableCell>
      <TableCell>
        <Checkbox
          aria-label="On sale"
          checked={draft.active}
          disabled={!isSuper}
          onCheckedChange={(checked) => set({ active: checked === true })}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span className="flex items-center gap-2">
          <Checkbox
            aria-label="Hidden from the order site"
            checked={draft.hidden}
            disabled={!isSuper}
            onCheckedChange={(checked) => set({ hidden: checked === true })}
          />
          {pkg.hidden && pkg.id ? (
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                navigator.clipboard
                  .writeText(`${ORDER_SITE}?package=${pkg.id}`)
                  .then(() => toast.success("Order link copied"))
              }
            >
              <LinkIcon data-icon="inline-start" /> Copy link
            </Button>
          ) : null}
        </span>
      </TableCell>
      <TableCell className="text-right">
        {isSuper && dirty ? (
          <Button size="sm" disabled={!valid || save.isPending} onClick={submit}>
            Save
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function PackagesTab({ isSuper }: { isSuper: boolean }) {
  const { data: packages = [], dataUpdatedAt } = useGiftpassPackages();
  const [adding, setAdding] = useState<Draft[]>([]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Prices here are live on the order site as soon as you save. Orders already open keep the
        price they started with. Every change is logged.
      </p>
      {isSuper ? null : <Callout>Only a Superadmin can change packages.</Callout>}
      <SurfaceCard className="overflow-x-auto p-0">
        <Table className="min-w-[56rem]">
          <TableHeader>
            <TableRow>
              <TableHead>Package</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Passes</TableHead>
              <TableHead>Free</TableHead>
              <TableHead>Buyer saves</TableHead>
              <TableHead>On sale</TableHead>
              <TableHead>Hidden</TableHead>
              <TableHead>
                <span className="sr-only">Save</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packages.map((p) => (
              // Remount on a refetch so a saved row drops its draft.
              <PackageRow key={`${p.id}-${dataUpdatedAt}`} pkg={toDraft(p)} isSuper={isSuper} />
            ))}
            {adding.map((d) => (
              <PackageRow
                key={`new-${d.sortOrder}`}
                pkg={d}
                isSuper={isSuper}
                onSaved={() => setAdding((list) => list.filter((x) => x !== d))}
              />
            ))}
          </TableBody>
        </Table>
      </SurfaceCard>
      {isSuper ? (
        <Button
          variant="outline"
          onClick={() =>
            setAdding((list) => [
              ...list,
              {
                id: null,
                name: "",
                price: "",
                passes: "1",
                freePasses: "0",
                active: false,
                hidden: true,
                sortOrder: packages.length + list.length + 1,
              },
            ])
          }
        >
          <PlusIcon data-icon="inline-start" /> Add package
        </Button>
      ) : null}
    </div>
  );
}
